// src/components/institution/sections/GalleriesSection.jsx
import React, { useState } from 'react';
import { 
  Image as ImageIcon, 
  Video, 
  Play, 
  ZoomIn, 
  Calendar, 
  MapPin, 
  Users,
  Grid,
  List,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  Camera,
  Eye,
  Tag,
  Edit2,
  Save,
  AlertCircle,
  Folder,
  FolderOpen
} from 'lucide-react';

// Photo Edit Modal Component (for use in lightbox)
const PhotoEditModal = ({ photo, isOpen, onClose, onSave, galleryTitle }) => {
  const [editedPhoto, setEditedPhoto] = useState({
    fileName: photo?.fileName || '',
    caption: photo?.caption || '',
    ...photo
  });
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (photo) {
      setEditedPhoto({
        fileName: photo.fileName || '',
        caption: photo.caption || '',
        ...photo
      });
    }
  }, [photo]);

  if (!isOpen || !photo) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(editedPhoto);
      onClose();
    } catch (error) {
      console.error('Error saving photo:', error);
      alert('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 " style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Edit Photo</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Photo Name *
            </label>
            <input
              type="text"
              value={editedPhoto.fileName}
              onChange={(e) => setEditedPhoto(prev => ({ ...prev, fileName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Enter photo name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={editedPhoto.caption}
              onChange={(e) => setEditedPhoto(prev => ({ ...prev, caption: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="Add a description..."
            />
          </div>

          <div className="bg-gray-50 p-3 rounded-lg text-sm">
            <p className="text-gray-600"><span className="font-medium">Gallery:</span> {galleryTitle}</p>
            <p className="text-gray-600"><span className="font-medium">Uploaded:</span> {new Date(photo.uploadedAt).toLocaleDateString()}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !editedPhoto.fileName.trim()}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const GalleriesSection = ({ 
  data, 
  settings, 
  styling, 
  galleries = [], 
  onGalleryClick,
  isOwner = false,
  onUpdateGallery
}) => {
  const [expandedGalleries, setExpandedGalleries] = useState({});
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [currentGalleryImages, setCurrentGalleryImages] = useState([]);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Toggle gallery expansion
  const toggleGallery = (galleryId) => {
    setExpandedGalleries(prev => ({
      ...prev,
      [galleryId]: !prev[galleryId]
    }));
  };

  // Handle photo editing
  const handleEditPhoto = (photo, galleryTitle) => {
    setEditingPhoto({ ...photo, galleryTitle });
    setShowEditModal(true);
  };

  // Save edited photo
  const handleSavePhoto = async (editedPhoto) => {
    if (!onUpdateGallery) {
      throw new Error('Gallery update not available');
    }

    const gallery = galleries.find(g => g.id === editedPhoto.galleryId);
    if (!gallery) {
      throw new Error('Gallery not found');
    }

    const updatedImages = gallery.images.map(img => 
      img.id === editedPhoto.id 
        ? { ...img, fileName: editedPhoto.fileName, caption: editedPhoto.caption }
        : img
    );

    await onUpdateGallery(gallery.id, { images: updatedImages });
    
    // Update current lightbox if this photo is selected
    if (selectedMedia && selectedMedia.id === editedPhoto.id) {
      setSelectedMedia({ ...selectedMedia, fileName: editedPhoto.fileName, caption: editedPhoto.caption });
    }
  };

  // Get category info
  const getCategoryInfo = (category) => {
    const categoryMap = {
      academic: { label: 'Academic', color: 'bg-blue-100 text-blue-700' },
      campus: { label: 'Campus Life', color: 'bg-green-100 text-green-700' },
      events: { label: 'Events', color: 'bg-purple-100 text-purple-700' },
      sports: { label: 'Sports', color: 'bg-orange-100 text-orange-700' },
      other: { label: 'General', color: 'bg-gray-100 text-gray-700' }
    };
    
    return categoryMap[category] || categoryMap.other;
  };

  // Handle media click
  const handleMediaClick = (media, gallery) => {
    const galleryImages = gallery.images.map(img => ({
      ...img,
      galleryId: gallery.id,
      galleryTitle: gallery.title,
      galleryCategory: gallery.category,
      type: img.fileType?.startsWith('video/') ? 'video' : 'image'
    }));
    
    setCurrentGalleryImages(galleryImages);
    setSelectedMedia({ ...media, galleryId: gallery.id, galleryTitle: gallery.title });
    setLightboxIndex(galleryImages.findIndex(img => img.id === media.id));
  };

  const closeLightbox = () => {
    setSelectedMedia(null);
    setCurrentGalleryImages([]);
  };

  const nextMedia = () => {
    const nextIndex = lightboxIndex < currentGalleryImages.length - 1 ? lightboxIndex + 1 : 0;
    setLightboxIndex(nextIndex);
    setSelectedMedia(currentGalleryImages[nextIndex]);
  };

  const prevMedia = () => {
    const prevIndex = lightboxIndex > 0 ? lightboxIndex - 1 : currentGalleryImages.length - 1;
    setLightboxIndex(prevIndex);
    setSelectedMedia(currentGalleryImages[prevIndex]);
  };

  // Render individual photo
  const renderPhoto = (image, gallery) => {
    const isVideo = image.fileType?.startsWith('video/');
    
    return (
      <div
        key={`${gallery.id}-${image.id}`}
        className="group relative bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer hover:shadow-md transition-shadow duration-200"
        onClick={() => handleMediaClick(image, gallery)}
      >
        <div className="aspect-w-1 aspect-h-1 bg-gray-100">
          {isVideo ? (
            <div className="relative w-full h-32 bg-gray-800 flex items-center justify-center">
              <Play className="h-6 w-6 text-white opacity-80" />
            </div>
          ) : (
            <img
              src={image.url}
              alt={image.fileName || 'Gallery image'}
              className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-200"
            />
          )}
          
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
            <ZoomIn className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
          </div>
        </div>
        
        {/* Photo Info */}
        <div className="p-2">
          <h4 className="font-medium text-gray-900 text-xs line-clamp-1">
            {image.fileName?.replace(/\.[^/.]+$/, "") || 'Untitled'}
          </h4>
          {image.caption && (
            <p className="text-xs text-gray-600 line-clamp-1 mt-1">
              {image.caption}
            </p>
          )}
        </div>
      </div>
    );
  };

  // Render gallery folder
  const renderGalleryFolder = (gallery) => {
    const categoryInfo = getCategoryInfo(gallery.category);
    const isExpanded = expandedGalleries[gallery.id];
    const FolderIcon = isExpanded ? FolderOpen : Folder;
    
    return (
      <div key={gallery.id} className="bg-white rounded-xl shadow-md overflow-hidden">
        {/* Gallery Folder Header */}
        <div 
          className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => toggleGallery(gallery.id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-100 rounded-lg">
                <FolderIcon className="h-6 w-6 text-gray-600" />
              </div>
              
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {gallery.title}
                  </h3>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${categoryInfo.color}`}>
                    {categoryInfo.label}
                  </span>
                </div>
                
                {gallery.description && (
                  <p className="text-gray-600 text-sm mb-2">
                    {gallery.description}
                  </p>
                )}
                
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <ImageIcon className="h-4 w-4" />
                    <span>{gallery.imageCount || gallery.images?.length || 0} photos</span>
                  </div>
                  {gallery.views > 0 && (
                    <div className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      <span>{gallery.views} views</span>
                    </div>
                  )}
                  {gallery.createdAt && (
                    <span>{new Date(gallery.createdAt.toDate()).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5" />
                ) : (
                  <ChevronDown className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Expanded Photo Grid */}
        {isExpanded && (
          <div className="border-t border-gray-100">
            {gallery.images && gallery.images.length > 0 ? (
              <div className="p-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {gallery.images.map((image) => 
                    renderPhoto(image, gallery)
                  )}
                </div>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                <ImageIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">No photos in this gallery yet</p>
              </div>
            )}
          </div>
        )}
        
        {/* Tags */}
        {gallery.tags && gallery.tags.length > 0 && (
          <div className="px-6 pb-4">
            <div className="flex flex-wrap gap-2">
              {gallery.tags.slice(0, 5).map((tag, index) => (
                <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                  <Tag className="h-3 w-3" />
                  {tag}
                </span>
              ))}
              {gallery.tags.length > 5 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                  +{gallery.tags.length - 5}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Enhanced Lightbox with editing
  const renderLightbox = () => {
    if (!selectedMedia) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center">
        {/* Top Controls */}
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
          <div className="text-white">
            <p className="text-sm opacity-75">
              {lightboxIndex + 1} of {currentGalleryImages.length}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {isOwner && (
              <button
                onClick={() => handleEditPhoto(selectedMedia, selectedMedia.galleryTitle)}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Edit2 className="h-4 w-4" />
                Edit
              </button>
            )}
            <button
              onClick={closeLightbox}
              className="p-2 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-lg transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Navigation buttons */}
        {currentGalleryImages.length > 1 && (
          <>
            <button
              onClick={prevMedia}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-lg transition-colors z-10"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
            <button
              onClick={nextMedia}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-lg transition-colors z-10"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          </>
        )}

        {/* Media content */}
        <div className="max-w-5xl max-h-screen w-full mx-4 flex flex-col">
          <div className="flex-1 flex items-center justify-center mt-16 mb-8">
            {selectedMedia.type === 'video' || selectedMedia.fileType?.startsWith('video/') ? (
              <video 
                src={selectedMedia.url} 
                controls 
                className="max-w-full max-h-[70vh] object-contain"
                autoPlay
              />
            ) : (
              <img
                src={selectedMedia.url}
                alt={selectedMedia.fileName}
                className="max-w-full max-h-[70vh] object-contain"
              />
            )}
          </div>
          
          {/* Media info */}
          <div className="text-white text-center px-4">
            <h3 className="text-xl font-semibold mb-2">
              {selectedMedia.fileName?.replace(/\.[^/.]+$/, "") || 'Untitled'}
            </h3>
            {selectedMedia.caption && (
              <p className="text-gray-300 mb-3">{selectedMedia.caption}</p>
            )}
            <div className="text-sm text-gray-400 space-x-4">
              <span>From: {selectedMedia.galleryTitle}</span>
              <span>•</span>
              <span>{new Date(selectedMedia.uploadedAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Fallback when no galleries exist
  if (!galleries || galleries.length === 0) {
    return (
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-16">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <Folder className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Photo Galleries</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Photo galleries showcasing campus life, events, and activities will be displayed here.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: styling?.primaryColor || '#1e40af' }}
          >
            Photo Galleries
          </h2>
          <div className="w-24 h-1 mx-auto mb-8" style={{ backgroundColor: styling?.primaryColor }} />
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explore our campus life, events, and memorable moments. Click on any gallery folder to view photos.
          </p>
        </div>

        {/* Gallery Folders */}
        <div className="space-y-6">
          {galleries.map(gallery => renderGalleryFolder(gallery))}
        </div>

        {/* Gallery Stats */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-600">
            {galleries.length} {galleries.length === 1 ? 'gallery' : 'galleries'} • {' '}
            {galleries.reduce((total, gallery) => total + (gallery.imageCount || gallery.images?.length || 0), 0)} total photos
          </p>
        </div>
      </div>

      {/* Lightbox Modal */}
      {renderLightbox()}

      {/* Photo Edit Modal */}
      <PhotoEditModal
        photo={editingPhoto}
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingPhoto(null);
        }}
        onSave={handleSavePhoto}
        galleryTitle={editingPhoto?.galleryTitle}
      />
    </section>
  );
};